import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import AppLayout from '../components/layout/AppLayout';
import DownloadFilesModal from '../components/ui/DownloadFilesModal';
import Icon from '../components/ui/Icon';
import Avatar from '../components/ui/Avatar';
import Chip from '../components/ui/Chip';
import Btn from '../components/ui/Btn';
import TimelineRow from '../components/ui/TimelineRow';

// ─── Constants ──────────────────────────────────────────
const TABS = [
  { id: 'action_required', label: 'Action Required', icon: 'pen' },
  { id: 'sent',            label: 'Sent',             icon: 'mail' },
  { id: 'expiring_soon',   label: 'Expiring Soon',    icon: 'alert' },
  { id: 'completed',       label: 'Completed',         icon: 'checkCircle' },
];

const PAGE_SIZES = [5, 10, 25, 50];

// ─── Helpers ─────────────────────────────────────────────
const groupIntoEnvelopes = (docs) => {
  const bundleMap = {};
  const standalones = [];

  for (const doc of docs) {
    if (doc.bundle_id) {
      if (!bundleMap[doc.bundle_id]) bundleMap[doc.bundle_id] = [];
      bundleMap[doc.bundle_id].push(doc);
    } else {
      standalones.push(doc);
    }
  }

  const envelopes = [];

  for (const [, bundleDocs] of Object.entries(bundleMap)) {
    const sorted = bundleDocs.sort((a, b) => (a.bundle_order || 0) - (b.bundle_order || 0));
    const first = sorted[0];
    const allCompleted  = sorted.every(d => d.status === 'completed');
    const anyCancelled  = sorted.some(d => d.status === 'cancelled');
    const anyInProgress = sorted.some(d => d.status === 'in_progress');
    let status = 'pending';
    if (allCompleted) status = 'completed';
    else if (anyCancelled) status = 'cancelled';
    else if (anyInProgress) status = 'in_progress';

    envelopes.push({
      envelopeId:           first.bundle_id,
      title:                first.bundle_title || first.title,
      status,
      documents:            sorted.map(d => ({ id: d.id, title: d.title, original_filename: d.original_filename })),
      doc_count:            sorted.length,
      recipient_count:      Math.max(...sorted.map(d => d.recipient_count || 0)),
      signed_count:         Math.min(...sorted.map(d => d.signed_count || 0)),
      total_pages:          sorted.reduce((s, d) => s + (d.total_pages || 0), 0),
      created_at:           first.created_at,
      created_at_formatted: first.created_at_formatted,
      expires_at:           first.expires_at,
      expires_at_formatted: first.expires_at_formatted,
      recipients:           first.recipients,
      primaryDocId:         first.id,
    });
  }

  for (const doc of standalones) {
    envelopes.push({
      envelopeId:           doc.id,
      title:                doc.title,
      status:               doc.status,
      documents:            [{ id: doc.id, title: doc.title, original_filename: doc.original_filename }],
      doc_count:            1,
      recipient_count:      doc.recipient_count || 0,
      signed_count:         doc.signed_count || 0,
      total_pages:          doc.total_pages || 0,
      created_at:           doc.created_at,
      created_at_formatted: doc.created_at_formatted,
      expires_at:           doc.expires_at,
      expires_at_formatted: doc.expires_at_formatted,
      recipients:           doc.recipients,
      primaryDocId:         doc.id,
    });
  }

  envelopes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return envelopes;
};

const getSigningType = (env) => (env.recipient_count <= 1 ? 'self' : 'group');

const getCompletionPercent = (env) => {
  if (env.status === 'completed') return 100;
  if (!env.recipient_count) return 0;
  return Math.round(((env.signed_count || 0) / env.recipient_count) * 100);
};

/** Convert envelope status + expiry into TimelineRow stages */
const buildStages = (env) => {
  const pct   = getCompletionPercent(env);
  const sent  = !!env.created_at;
  const viewed = env.status === 'in_progress' || env.status === 'completed' || pct > 0;
  const signing = env.status === 'in_progress';
  const done  = env.status === 'completed';
  const blocked = env.status === 'cancelled';

  return [
    { label: 'Sent',      meta: env.created_at_formatted || '—', state: sent ? 'done' : 'pending' },
    { label: 'Viewed',    meta: '',  state: viewed ? (done ? 'done' : 'done') : 'pending' },
    { label: 'Signing',   meta: pct > 0 && !done ? `${env.signed_count}/${env.recipient_count}` : '',
      state: blocked ? 'blocked' : done ? 'done' : signing ? 'active' : 'pending' },
    { label: 'Completed', meta: '', state: done ? 'done' : 'pending' },
  ];
};

/** Days until expiry string */
const expiresLabel = (env) => {
  if (!env.expires_at) return null;
  const diff = Math.ceil((new Date(env.expires_at) - new Date()) / 86400000);
  if (diff <= 0) return 'Expired';
  if (diff <= 7) return `${diff}d left`;
  return `${diff}d`;
};

// ─── Main Component ──────────────────────────────────────
const DashboardPage = () => {
  const navigate = useNavigate();
  useAuth(); // auth context (logout handled by AppLayout)
  const [envelopes,    setEnvelopes]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('action_required');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [showNewMenu,  setShowNewMenu]  = useState(false);
  const [downloadEnv,  setDownloadEnv]  = useState(null);
  const [allEnvelopes, setAllEnvelopes] = useState([]); // merged own + signer envelopes
  const newMenuRef = useRef(null);

  useEffect(() => { loadDocuments(); }, [activeTab]); // eslint-disable-line
  useEffect(() => {
    const h = (e) => { if (newMenuRef.current && !newMenuRef.current.contains(e.target)) setShowNewMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // ── Own documents (created by this user) ──
      const res         = await api.getDocuments(1, 200);
      const ownEnvelopes = groupIntoEnvelopes(res.documents || []);
      const ownDocIds   = new Set(ownEnvelopes.map(e => e.primaryDocId));

      // ── Documents where user is a recipient (SaaS / cross-server) ──
      let signerEnvelopes = [];
      try {
        const toSignRes    = await api.getDocumentsToSign();
        const signerDocsRaw = toSignRes.documents || [];
        signerEnvelopes = signerDocsRaw
          .filter(d => !ownDocIds.has(d.document_id)) // deduplicate
          .map(d => ({
            envelopeId:           d.document_id,
            title:                d.document_title,
            status:               d.document_status,
            documents:            [{ id: d.document_id, title: d.document_title, original_filename: d.document_title }],
            doc_count:            1,
            recipient_count:      d.total_recipients,
            signed_count:         d.signed_count,
            total_pages:          0,
            created_at:           d.created_at,
            created_at_formatted: d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : '—',
            expires_at:           d.expires_at,
            expires_at_formatted: d.expires_at ? new Date(d.expires_at).toLocaleDateString('en-GB') : '—',
            recipients:           [],
            primaryDocId:         d.document_id,
            _isSignerDoc:         true,
            _recipientStatus:     d.recipient_status,   // 'pending' | 'signed' | 'declined'
            _isMyTurn:            d.is_my_turn,
            _signingToken:        d.signing_token,
            _signingOrder:        d.signing_order,
          }));
      } catch { /* non-fatal */ }

      // ── Merge + sort by created_at ──
      const all = [...ownEnvelopes, ...signerEnvelopes]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllEnvelopes(all);

      // ── Tab filter ──
      let filtered;
      if (activeTab === 'completed') {
        filtered = all.filter(e =>
          e.status === 'completed' ||
          (e._isSignerDoc && e._recipientStatus === 'signed')
        );
      } else if (activeTab === 'sent') {
        filtered = all.filter(e =>
          !e._isSignerDoc && (e.status === 'pending' || e.status === 'in_progress')
        );
      } else if (activeTab === 'expiring_soon') {
        const soon = new Date(Date.now() + 7 * 86400000);
        filtered = all.filter(e =>
          e.expires_at && new Date(e.expires_at) <= soon &&
          e.status !== 'completed' && e.status !== 'cancelled'
        );
      } else {
        // Action Required: own pending envelopes + signerDocs still waiting to be signed
        filtered = all.filter(e =>
          (!e._isSignerDoc && ['pending', 'in_progress', 'draft'].includes(e.status)) ||
          (e._isSignerDoc && e._recipientStatus === 'pending')
        );
      }
      setEnvelopes(filtered);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Summary strip counts
  const awaiting   = allEnvelopes.filter(e => e.status === 'in_progress').length;
  const readyCount = allEnvelopes.filter(e => e.status === 'pending').length;
  const soon       = new Date(Date.now() + 7 * 86400000);
  const expiringN  = allEnvelopes.filter(e => e.expires_at && new Date(e.expires_at) <= soon && e.status !== 'completed' && e.status !== 'cancelled').length;
  const completed30 = allEnvelopes.filter(e => {
    if (e.status !== 'completed') return false;
    const d = new Date(e.created_at);
    return (Date.now() - d.getTime()) < 30 * 86400000;
  }).length;

  const SUMMARY = [
    { label: 'Awaiting others', value: awaiting,   delta: '+2 this week', icon: 'clock',       tone: 'text-ink-700' },
    { label: 'Action required', value: readyCount,  delta: 'Needs you',   icon: 'pen',         tone: 'text-leaf-700' },
    { label: 'Expiring < 7d',   value: expiringN,  delta: 'Act now',      icon: 'alert',       tone: 'text-amber-600' },
    { label: 'Completed (30d)', value: completed30, delta: 'Total',        icon: 'checkCircle', tone: 'text-leaf-700' },
  ];

  // Search filter
  const filteredEnvelopes = searchQuery
    ? envelopes.filter(e =>
        (e.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(e.envelopeId || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : envelopes;

  // Pagination
  const totalCount   = filteredEnvelopes.length;
  const totalPages   = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginated    = filteredEnvelopes.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageRange    = () => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const tabCount = (id) => {
    if (id === 'completed')
      return allEnvelopes.filter(e =>
        e.status === 'completed' || (e._isSignerDoc && e._recipientStatus === 'signed')
      ).length;
    if (id === 'sent')
      return allEnvelopes.filter(e => !e._isSignerDoc && ['pending','in_progress'].includes(e.status)).length;
    if (id === 'expiring_soon') return expiringN;
    // Action Required
    return allEnvelopes.filter(e =>
      (!e._isSignerDoc && ['pending','in_progress','draft'].includes(e.status)) ||
      (e._isSignerDoc && e._recipientStatus === 'pending')
    ).length;
  };

  return (
    <AppLayout fullHeight activeNav="signing">
      <main className="flex-1 overflow-hidden px-6 lg:px-8 py-6">
        <div className="h-full flex flex-col gap-5 max-w-[1440px] mx-auto">

          {/* ── Page header ─────────────────────────────── */}
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-leaf-700 mb-1">Signing center</div>
              <h1 className="font-display text-[28px] sm:text-[32px] leading-none font-bold text-ink-900">All envelopes</h1>
              <p className="text-[13px] text-ink-500 mt-1.5">Review, complete, and manage every signature across your advisory practice.</p>
            </div>

            {/* New signing dropdown */}
            <div className="relative flex-shrink-0" ref={newMenuRef}>
              <Btn
                icon="plus"
                trailing="chevronDown"
                onClick={() => setShowNewMenu(v => !v)}
              >
                New signing
              </Btn>
              {showNewMenu && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-float ring-1 ring-ink-100 z-20 py-1.5 text-sm">
                  <button
                    onClick={() => { setShowNewMenu(false); navigate('/documents/new?type=group'); }}
                    className="w-full text-left px-4 py-2 text-ink-700 hover:bg-paper-200 flex items-center gap-2"
                  >
                    <Icon name="users" size={14} className="text-ink-400" /> Group signing
                  </button>
                  <button
                    onClick={() => { setShowNewMenu(false); navigate('/documents/new?type=self'); }}
                    className="w-full text-left px-4 py-2 text-ink-700 hover:bg-paper-200 flex items-center gap-2"
                  >
                    <Icon name="user" size={14} className="text-ink-400" /> Self signing
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Summary strip ────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {SUMMARY.map((s, i) => (
              <div key={i} className="bg-paper-100 rounded-xl2 ring-1 ring-ink-100 shadow-soft p-4 flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg bg-white ring-1 ring-ink-100 flex items-center justify-center flex-shrink-0 ${s.tone}`}>
                  <Icon name={s.icon} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-ink-400 uppercase tracking-wider font-medium leading-none mb-0.5">{s.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-display font-bold text-[26px] text-ink-900 tnum leading-none">{s.value}</span>
                    <span className={`text-[11px] ${s.tone}`}>{s.delta}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Main card ────────────────────────────────── */}
          <div className="flex-1 bg-paper-100 rounded-2xl ring-1 ring-ink-100 shadow-soft flex flex-col overflow-hidden min-h-0">

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-ink-100">
              <div className="flex items-center gap-5">
                {TABS.map(t => {
                  const cnt   = tabCount(t.id);
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setActiveTab(t.id); setCurrentPage(1); }}
                      className={`relative pb-3 text-[13px] font-semibold flex items-center gap-2 transition-colors ${
                        isActive ? 'text-ink-900' : 'text-ink-400 hover:text-ink-700'
                      }`}
                    >
                      <span>{t.label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] tnum ${
                        isActive ? 'bg-leaf-500 text-white' :
                        t.id === 'expiring_soon' ? 'bg-amber-50 text-amber-600' :
                        'bg-ink-50 text-ink-500'
                      }`}>{cnt}</span>
                      {isActive && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-leaf-500 rounded-full" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Filter bar */}
            <div className="px-6 py-3 flex items-center justify-between border-b border-ink-100 bg-white/50 gap-3">
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Icon name="search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    placeholder="Search by ID, subject, or recipient…"
                    className="w-full h-9 pl-9 pr-3 rounded-lg bg-white ring-1 ring-inset ring-ink-200 focus:ring-leaf-500 outline-none text-[13px] text-ink-800"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-600">
                      <Icon name="x" size={13} />
                    </button>
                  )}
                </div>
                <Btn tone="outline" size="sm" icon="filter" trailing="chevronDown">Type</Btn>
                <Btn tone="outline" size="sm" icon="calendar" trailing="chevronDown">Date</Btn>
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-ink-500 flex-shrink-0">
                <span>Sort:</span>
                <button className="font-semibold text-ink-800 inline-flex items-center gap-1">
                  Most recent <Icon name="chevronDown" size={12} />
                </button>
              </div>
            </div>

            {/* Table + signerDocs — all inside the scrollable area */}
            <div className="flex-1 overflow-auto thin-scroll">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
                  {[1,2,3].map(i => (
                    <div key={i} className="flex items-center gap-3 w-full max-w-2xl px-6">
                      <div className="w-9 h-9 rounded-lg bg-ink-50 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-ink-50 rounded animate-pulse" style={{ width: '55%' }} />
                        <div className="h-2 bg-ink-50 rounded animate-pulse" style={{ width: '30%' }} />
                      </div>
                      <div className="w-48 h-2 bg-ink-50 rounded-full animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* ── Envelope table ── */}
                  {filteredEnvelopes.length === 0 ? null : (
                <table className="w-full">
                  <thead className="bg-paper-200/40 sticky top-0 backdrop-blur">
                    <tr className="border-b border-ink-100">
                      <Th>Envelope</Th>
                      <Th>Type</Th>
                      <Th>Recipients</Th>
                      <Th className="w-[300px] xl:w-[360px]">Progress</Th>
                      <Th>Created</Th>
                      <Th>Expires</Th>
                      <Th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((env) => {
                      const sigType  = getSigningType(env);
                      const stages   = buildStages(env);
                      const expLabel = expiresLabel(env);
                      const recips   = env.recipients || [];
                      const waiting  = recips.find(r => r.status === 'pending');

                      return (
                        <tr
                          key={env.envelopeId}
                          className="border-b border-ink-50 hover:bg-paper-200/50 group transition-colors cursor-pointer"
                          onClick={() => navigate(`/documents/${env.primaryDocId}`)}
                        >
                          {/* Envelope name + ID */}
                          <td className="px-5 py-4 align-top">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-white ring-1 ring-ink-100 flex items-center justify-center text-ink-400 flex-shrink-0 mt-0.5">
                                <Icon name={env.doc_count > 1 ? 'stack' : 'file'} size={15} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-[13.5px] font-semibold text-ink-900 leading-tight truncate max-w-[220px] xl:max-w-[280px]">
                                  {env.title}
                                </div>
                                <div className="text-[11px] text-ink-400 mt-0.5 font-mono tnum">
                                  {String(env.envelopeId).slice(0, 12)} · {env.doc_count} {env.doc_count === 1 ? 'doc' : 'docs'}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Type chip */}
                          <td className="px-3 py-4 align-top">
                            <Chip icon={sigType === 'group' ? 'users' : 'user'}>
                              {sigType === 'group' ? 'Group' : 'Self'}
                            </Chip>
                          </td>

                          {/* Recipients */}
                          <td className="px-3 py-4 align-top">
                            <div className="flex items-center gap-2">
                              <div className="flex -space-x-1.5">
                                {recips.slice(0, 3).map((r, k) => (
                                  <Avatar key={k} name={r.name || r.email} size={22} ring />
                                ))}
                                {recips.length === 0 && (
                                  <span className="inline-flex items-center justify-center w-[22px] h-[22px] text-[9px] font-semibold text-ink-500 bg-ink-50 rounded-full ring-2 ring-white tnum">
                                    {env.recipient_count}
                                  </span>
                                )}
                                {recips.length > 3 && (
                                  <span className="inline-flex items-center justify-center w-[22px] h-[22px] text-[9px] font-semibold text-ink-600 bg-paper-200 ring-2 ring-white rounded-full tnum">
                                    +{recips.length - 3}
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-ink-500 tnum">{env.signed_count}/{env.recipient_count}</span>
                            </div>
                            {waiting && (
                              <div className="text-[11px] text-ink-400 mt-1 truncate max-w-[160px]">
                                Waiting · <span className="text-ink-700 font-medium">{waiting.name || waiting.email}</span>
                              </div>
                            )}
                          </td>

                          {/* Timeline progress */}
                          <td className="px-3 py-4 align-top">
                            <TimelineRow stages={stages} compact />
                          </td>

                          {/* Created */}
                          <td className="px-3 py-4 align-top text-[12px] text-ink-600 tnum whitespace-nowrap">
                            {env.created_at_formatted || '—'}
                          </td>

                          {/* Expires / status — signer docs show recipient status chip */}
                          <td className="px-3 py-4 align-top whitespace-nowrap">
                            {env._isSignerDoc ? (
                              <>
                                {env._recipientStatus === 'signed'   && <Chip tone="leaf"  icon="checkCircle">Signed</Chip>}
                                {env._recipientStatus === 'pending' && env._isMyTurn  && <Chip tone="amber" icon="pen">Action Required</Chip>}
                                {env._recipientStatus === 'pending' && !env._isMyTurn && <Chip tone="ink"   icon="clock">Waiting</Chip>}
                                {env._recipientStatus === 'declined' && <Chip tone="flame" icon="ban">Declined</Chip>}
                              </>
                            ) : (
                              <>
                                {env.status === 'completed' && <Chip tone="leaf"  icon="checkCircle">Completed</Chip>}
                                {env.status === 'cancelled' && <Chip tone="flame" icon="ban">Cancelled</Chip>}
                                {env.status !== 'completed' && env.status !== 'cancelled' && expLabel && (
                                  Number(expLabel.replace('d left','').replace('d','')) <= 7 && !expLabel.includes('Expired')
                                    ? <Chip tone="amber" icon="alert">{expLabel}</Chip>
                                    : <span className="text-[12px] text-ink-500 tnum">{expLabel}</span>
                                )}
                              </>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-3 py-4 align-top">
                            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Sign Now — signer doc, my turn */}
                              {env._isSignerDoc && env._isMyTurn && env._recipientStatus === 'pending' && env._signingToken && (
                                <button
                                  onClick={e => { e.stopPropagation(); window.open(`/sign/${env._signingToken}`, '_blank'); }}
                                  className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-semibold rounded-lg transition-colors whitespace-nowrap"
                                >
                                  Sign →
                                </button>
                              )}
                              {/* Download — completed owner doc */}
                              {!env._isSignerDoc && env.status === 'completed' && (
                                <button
                                  onClick={e => { e.stopPropagation(); setDownloadEnv({ documents: env.documents, title: env.title }); }}
                                  className="p-1.5 rounded-md text-ink-400 hover:text-ink-800 hover:bg-white"
                                  title="Download"
                                >
                                  <Icon name="download" size={14} />
                                </button>
                              )}
                              {/* Void — owner doc, not completed/cancelled */}
                              {!env._isSignerDoc && env.status !== 'completed' && env.status !== 'cancelled' && (
                                <button
                                  onClick={async e => {
                                    e.stopPropagation();
                                    if (window.confirm('Void this envelope?')) {
                                      try { for (const d of env.documents) await api.cancelDocument(d.id); loadDocuments(); } catch {}
                                    }
                                  }}
                                  className="p-1.5 rounded-md text-ink-400 hover:text-flame-600 hover:bg-flame-50"
                                  title="Void"
                                >
                                  <Icon name="ban" size={14} />
                                </button>
                              )}
                              <button className="p-1.5 rounded-md text-ink-400 hover:text-ink-800 hover:bg-white">
                                <Icon name="more" size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                  )}
                </>
              )}
            </div>

            {/* Pagination footer */}
            {!loading && filteredEnvelopes.length > 0 && (
              <div className="px-6 py-3 border-t border-ink-100 flex items-center justify-between text-[12px] text-ink-500 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span>Rows</span>
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="h-7 px-2 rounded ring-1 ring-ink-200 bg-white font-medium text-ink-700 text-[12px] outline-none"
                  >
                    {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className="text-ink-400">of {totalCount} envelopes</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="w-7 h-7 rounded hover:bg-white text-ink-400 disabled:opacity-30"
                  >
                    <Icon name="chevronLeft" size={14} />
                  </button>
                  {pageRange().map(p => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-7 h-7 rounded text-[12px] tnum font-medium ${
                        currentPage === p ? 'bg-ink-700 text-white' : 'hover:bg-white text-ink-600'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="w-7 h-7 rounded hover:bg-white text-ink-400 disabled:opacity-30"
                  >
                    <Icon name="chevronRight" size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Download modal */}
      {downloadEnv && (
        <DownloadFilesModal
          documents={downloadEnv.documents}
          envelopeTitle={downloadEnv.title}
          onClose={() => setDownloadEnv(null)}
        />
      )}
    </AppLayout>
  );
};

// ── Table header cell ────────────────────────────────────
const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-400 ${className}`}>
    {children}
  </th>
);

export default DashboardPage;
